import {useEffect, useState, useCallback, useRef} from "react";
import {DEFAULT_PAGE_SIZE_OPTIONS} from "../utils/pagination";

export default function usePaginationSearch({
  initialFilters = {},
  initialItemsPerPage = DEFAULT_PAGE_SIZE_OPTIONS[0],
  initialSortModel = [],
  initialListState,
  fetcher
}) {
  const restoredFilters = initialListState?.filters ?? initialFilters;
  const restoredPaginationModel = initialListState?.paginationModel ?? {page: 0, pageSize: initialItemsPerPage};
  const restoredSortModel = initialListState?.sortModel ?? initialSortModel;

  const [filters, setFilters] = useState(restoredFilters);
  const [paginationModel, setPaginationModel] = useState(restoredPaginationModel);
  const [sortModel, setSortModel] = useState(restoredSortModel);
  const [query, setQuery] = useState(() => {
    const sort = restoredSortModel[0];
    return {
      page: restoredPaginationModel.page,
      limit: restoredPaginationModel.pageSize,
      ...(sort ? {orderBy: sort.field, orderDirection: sort.sort.toUpperCase()} : {}),
      ...restoredFilters
    };
  });

  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(
    () => (restoredPaginationModel.page + 1) * restoredPaginationModel.pageSize
  );

  const fetcherRef = useRef(fetcher);
  const previousSortModelRef = useRef(sortModel);

  useEffect(() => {
    setQuery((prev) => {
      const newPage = paginationModel.page;
      const newLimit = paginationModel.pageSize;
      if (prev.page === newPage && prev.limit === newLimit) return prev;
      return {...prev, page: newPage, limit: newLimit};
    });
  }, [paginationModel]);

  useEffect(() => {
    const sortModelChanged = previousSortModelRef.current !== sortModel;
    previousSortModelRef.current = sortModel;
    if (!sortModelChanged) return;

    setQuery((prev) => {
      const sort = sortModel[0];
      const orderBy = sort?.field;
      const orderDirection = sort ? sort.sort.toUpperCase() : undefined;
      if (prev.orderBy === orderBy && prev.orderDirection === orderDirection) return prev;
      const next = {...prev, page: 0};
      if (orderBy && orderDirection) {
        next.orderBy = orderBy;
        next.orderDirection = orderDirection;
      } else {
        delete next.orderBy;
        delete next.orderDirection;
      }
      return next;
    });
    setPaginationModel((prev) => ({...prev, page: 0}));
  }, [sortModel]);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    fetcherRef.current(query).then((response) => {
      if (!response) return;
      const {data} = response;
      setRows(data?.data);
      setTotalRows(data?.pagination?.totalItems || 0);
    });
  }, [query]);

  const refresh = useCallback(() => {
    fetcherRef.current(query).then((response) => {
      if (!response || response.success === false) return;
      const {data} = response;
      setRows(data?.data);
      setTotalRows(data?.pagination?.totalItems || 0);
    });
  }, [query]);

  const setFilter = (key, value) => {
    if (value === undefined || value === null || value === "") {
      setFilters((prev) => {
        const newFilters = {...prev};
        delete newFilters[key];
        return newFilters;
      });
    } else {
      setFilters((prev) => ({...prev, [key]: value}));
    }
  };

  const applyFilters = useCallback(
    (filtersObj) => {
      const isPlainObject = filtersObj != null && typeof filtersObj === "object" && !filtersObj.nativeEvent;
      const active = isPlainObject ? filtersObj : filters;
      if (isPlainObject) setFilters(filtersObj);
      setQuery((prev) => ({page: 0, limit: prev.limit, orderBy: prev.orderBy, orderDirection: prev.orderDirection, ...active}));
      setPaginationModel((prev) => ({...prev, page: 0}));
    },
    [filters]
  );

  const resetFilters = () => {
    setFilters(initialFilters);
    setQuery((prev) => ({page: 0, limit: prev.limit, orderBy: prev.orderBy, orderDirection: prev.orderDirection, ...initialFilters}));
    setPaginationModel((prev) => ({...prev, page: 0}));
  };

  const dataGridProps = {
    rows,
    rowCount: totalRows,
    paginationMode: "server",
    sortingMode: "server",
    filterMode: "server",
    pagination: true,
    paginationModel,
    onPaginationModelChange: setPaginationModel,
    sortModel,
    onSortModelChange: setSortModel
  };

  return {
    filters,
    setFilter,
    applyFilters,
    applyFiltersFromObject: applyFilters,
    resetFilters,
    refresh,
    dataGridProps,
    listState: {filters, paginationModel, sortModel}
  };
}
